import React from 'react';

interface NavigationProps {
  /** 
   * Indicates whether the page has been scrolled past a certain threshold.
   * When `true` the navigation bar gets a solid background and darker text,
   * otherwise it stays transparent with light‑colored text.
   */
  scrolled: boolean;
}

/**
 * Navigation component – a responsive header that adapts its style
 * based on the `scrolled` prop.
 *
 * The component is deliberately defensive:
 * - It validates the `scrolled` prop type via TypeScript.
 * - It falls back to safe default classes if an unexpected value is passed.
 * - All dynamic class concatenations are wrapped in a helper to avoid
 *   malformed strings.
 */
export default function Navigation({ scrolled }: NavigationProps) {
  /**
   * Helper to safely build Tailwind class strings.
   * Guarantees that the returned string is never `undefined` or `null`.
   */
  const cls = (...classes: (string | false | null | undefined)[]) =>
    classes.filter(Boolean).join(' ');

  return (
    <nav
      className={cls(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled ? 'bg-[#E2D9CA] shadow-md' : 'bg-transparent'
      )}
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img
            src="https://public.readdy.ai/ai/img_res/269a2b39-9248-4bf4-bc27-6f4e4990cd54.png"
            alt="ABEXIA Logo"
            className={cls(
              'h-12 md:h-24 w-auto object-contain transition-all duration-300',
              !scrolled && 'brightness-0 invert'
            )}
          />
        </div>

        {/* Desktop navigation links */}
        <div className="hidden md:flex items-center gap-8">
          {['collections', 'party-bags', 'about', 'contact'].map((section) => (
            <a
              key={section}
              href={`#${section}`}
              className={cls(
                'relative group whitespace-nowrap text-base font-medium cursor-pointer transition-colors duration-300 hover:text-[#EB9830]',
                scrolled ? 'text-[#936A0B]' : 'text-white'
              )}
            >
              {section
                .split('-')
                .map((s) => s[0].toUpperCase() + s.slice(1))
                .join(' ')}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#EB9830] transition-all duration-300 group-hover:w-full"></span>
            </a>
          ))}
        </div>

        {/* Action icons */}
        <div className="flex items-center gap-4 md:gap-6">
          <button
            type="button"
            className={cls(
              'w-5 h-5 md:w-6 md:h-6 flex items-center justify-center transition-colors cursor-pointer hover:text-[#EB9830]',
              scrolled ? 'text-[#936A0B]' : 'text-white'
            )}
            aria-label="Search"
          >
            <i className="ri-search-line text-lg md:text-xl" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </nav>
  );
}
